import { FC } from 'react';
import Button from '../../atoms/Button';
import LoadingWheel from '../../atoms/LoadingWheel';
import IconButton from '../IconButton';
import CrossIcon from '../../atoms/CrossIcon';
import styles from './Modal.module.css';

type ModalProps = {
  title: string;
  description: string | JSX.Element;
  secondaryCTALabel?: string;
  primaryCTALabel?: string;
  onSecondaryCTAClick?: () => void;
  onPrimaryCTAClick?: () => void;
  isShowSecondaryButton?: boolean;
  isShowPrimaryButton?: boolean;
  showSpinner?: boolean;
  isDisabled?: boolean;
  showSpinnerOnSecondaryCTA?: boolean;
  showCloseIcon?: boolean;
  onCloseClick?: () => void;
  onClose?: () => void;
  isOpen?: boolean;
};

const Modal: FC<ModalProps> = ({
  title,
  description,
  secondaryCTALabel,
  primaryCTALabel,
  isShowSecondaryButton = true,
  isShowPrimaryButton = true,
  onPrimaryCTAClick = () => {},
  onSecondaryCTAClick = () => {},
  showSpinner = false,
  isDisabled = false,
  showSpinnerOnSecondaryCTA = false,
  showCloseIcon = true,
  onCloseClick,
  onClose,
  isOpen = true,
}) => {
  const showButtonContainer = isShowPrimaryButton || isShowSecondaryButton;

  // Default onCloseClick to onClose if not provided
  const handleCloseClick = () => {
    if (onCloseClick) {
      onCloseClick();
    } else if (onClose) {
      onClose();
    }
  };

  const handleBackdropClick = (e: React.MouseEvent<HTMLDivElement>) => {
    // Only close if clicking on the backdrop itself, not the modal content
    if (e.target === e.currentTarget) {
      handleCloseClick();
    }
  };

  if (!isOpen) return null;

  return (
    <div
      id='modal'
      className={`${styles.modal} ${styles['modal--backdrop']}`}
      aria-labelledby={title}
      tabIndex={-1}
      onClick={handleBackdropClick}
    >
      <div className={styles.modal__content}>
        {showCloseIcon && (
          <IconButton
            className={`${styles.modal__close} tab-accessible`}
            onClick={handleCloseClick}
            ariaLabel='Close dialog box'
          >
            <CrossIcon />
          </IconButton>
        )}
        <div className={styles.modal__body}>
          <h2 tabIndex={0} className={styles.modal__title}>
            {title}
          </h2>
          <p tabIndex={0} className={styles.modal__description}>
            {description}
          </p>
        </div>
        {showButtonContainer && (
          <div className={styles['modal__button--container']}>
            {isShowSecondaryButton && secondaryCTALabel && (
              <Button
                variant='ghost'
                onClick={onSecondaryCTAClick}
                type='button'
                colorScheme='light'
                className={styles['modal__button--secondary']}
                isDisabled={isDisabled}
              >
                {showSpinnerOnSecondaryCTA && (
                  <LoadingWheel size={16} className={styles.modal__spinner} />
                )}
                {secondaryCTALabel}
              </Button>
            )}
            {isShowPrimaryButton && primaryCTALabel && (
              <Button
                onClick={onPrimaryCTAClick}
                type='button'
                colorScheme='primary'
                variant='solid'
                className={styles['modal__button--primary']}
                isDisabled={isDisabled}
              >
                {showSpinner && (
                  <LoadingWheel size={16} className={styles.modal__spinner} />
                )}
                {primaryCTALabel}
              </Button>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default Modal;
